import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import {ajaxPageNum, urlParamPrefix} from "@/common/config";
import _isObject from 'lodash/isObject';
import {AUTH_ERROR} from "@/common/errorcodes";

Vue.use(VueAxios, axios);

const ApiService = {
	/**
	 * Dispatch GET Request
	 * @param controller
	 * @param action
	 * @param params
	 * @param axiosParams
	 * @returns {Promise<T | never>}
	 */
	get(controller, action, params = {}, axiosParams = {}) {
		return this.handleResponse(Vue.axios.get(this.buildUrl(controller, action, params), axiosParams));
	},

	/**
	 * Dispatch POST Request
	 * @param controller
	 * @param action
	 * @param params
	 * @returns {AxiosPromise<any>}
	 */
	post(controller, action, params = {}) {
		return this.handleResponse(Vue.axios.post(this.buildUrl(controller, action), this.getRequestParams(params)));
	},

	handleResponse(response) {
		response
			.then(({data}) => {
				if (data && data.status === AUTH_ERROR) {
					window.location.reload();
				}
			})
			.catch(error => {
				if(_isObject(error)) {
					throw error
				} else {
					throw new Error(error);
				}
			});

		return response;
	},

	/**
	 * Redirect main browser window to generated url
	 * @param controller
	 * @param action
	 * @param params
	 */
	redirect(controller, action, params = {}) {
		window.location.href = this.buildUrl(controller, action, params);
	},

	buildUrl(controller, action, params = {}) {
		let url = `
			?type=${ajaxPageNum}
			&${urlParamPrefix}[controller]=${controller}
			&${urlParamPrefix}[action]=${action}
		`;

		if (params) {
			const urlParams = this.getRequestParams(params).toString();

			if (urlParams.length) {
				url += `&${urlParams}`;
			}
		}

		return url;
	},

	/**
	 * Build POST Parameters for request
	 * @param params
	 * @returns {URLSearchParams}
	 */
	getRequestParams(params) {
		let urlParams = new URLSearchParams();

		if (params) {
			this.appendParams(urlParams, params);
		}

		return urlParams;
	},

	appendParams(urlParams, params, path = '') {
		Object.entries(params).forEach((param) => {
			if (_isObject(param[1]) && !param[1].hasOwnProperty('uid')) {
				// param is an array of params
				this.appendParams(urlParams, param[1], `${path}[${param[0]}]`);
			} else {
				// param is single value or model object
				urlParams.append(
					`${urlParamPrefix}${path}[${param[0]}]`,
					(_isObject(param[1]) && param[1].hasOwnProperty('uid') ? param[1]['uid'] : param[1])
				)
			}
		});
	}
};

export default ApiService;

export const UserService = {
	auth() {
		return ApiService.get('User', 'auth');
	},
	getCompanyUsers() {
		return ApiService.get('User', 'companyUsers');
	}
};

export const SpeciesService = {
	get() {
		return ApiService.get('WoodSpecies', 'list');
	},
	getSpeciesFilters() {
		return ApiService.get('WoodSpecies', 'speciesFilters');
	}
};

export const SearchService = {
	get(params, axiosParams) {
		return ApiService.get('Search', 'list', {params}, axiosParams);
	},
	find(identifier) {
		return ApiService.get('Search', 'show', {identifier});
	},
	getBundles(identifier) {
		return ApiService.get('Search', 'bundles', {identifier});
	},
	getFilterData(species) {
		return ApiService.get('Search', 'getFilterData', {species});
	}
};

export const WatchlistService = {
	get(params) {
		return ApiService.get('Watchlist', 'list', {params});
	},
	find(params) {
		return ApiService.get('Watchlist', 'show', params);
	},
	findItem(params) {
		return ApiService.get('Watchlist', 'showItem', params);
	},
	findByItem(params) {
		return ApiService.get('Watchlist', 'findWatchlistByItem', params);
	},
	create(params) {
		return ApiService.post('Watchlist', 'create', {params})
	},
	add({watchlist, objects, params}) {
		return ApiService.post('Watchlist', 'add', {
			watchlist,
			objects: JSON.stringify(objects),
			params
		});
	},
	merge(params) {
		return ApiService.post('Watchlist', 'merge', params);
	},
	update(watchlist, updatedProperties) {
		return ApiService.post('Watchlist', 'update', {
			watchlist,
			updatedProperties
		});
	},
	delete(watchlist) {
		return ApiService.post('Watchlist', 'delete', {watchlist});
	},
	remove(params) {
		return ApiService.post('Watchlist', 'remove', params);
	},
	updateItem(watchlistItem, watchlist, updatedProperties) {
		return ApiService.post('Watchlist', 'updateItem', {
			watchlistItem,
			watchlist,
			updatedProperties
		});
	},
	createWatchlistFromSearchParams(searchParams) {
		return ApiService.post('Watchlist', 'createFromSearchParams', {searchParams});
	},
	updateWatchlistItemsFromApi(watchlist) {
		return ApiService.get('Watchlist', 'updateWatchlistItemsFromApi', {watchlist});
	},
	findWatchlistsWithItem(logId) {
		return ApiService.get('Watchlist', 'findWatchlistsWithItem', {logId});
	}
};

export const VirtualLogService = {
	find(identifier) {
		return ApiService.get('VirtualLog', 'show', {identifier});
	},
	update({log, watchlist, imageUrls}) {
		let params = {
			log: JSON.stringify(log),
			imageUrls
		};

		if (watchlist) {
			params = {
				...params,
				watchlist: watchlist.uid
			}
		}

		return ApiService.post('VirtualLog', 'update', params);
	},
	getBundles(identifier) {
		return ApiService.get('VirtualLog', 'bundles', {log: identifier});
	}
};

export const ZohoService = {
	searchCustomers(searchWord) {
		return ApiService.get('Zoho', 'getCustomerRecords', {searchWord});
	}
};

export const ExportService = {
	downloadMeasurementList(params) {
		return ApiService.redirect('Export', 'downloadMeasurementList', params);
	},
	downloadManualImages(params) {
		return ApiService.redirect('Export', 'downloadManualImages', params);
	},
	downloadWatchlistSummary(params) {
		return ApiService.redirect('Export', 'downloadWatchlistSummary', params);
	}
};

export const PalletService = {
	getLogsByPalletIdentifiers(identifiers) {
		return ApiService.get('Pallet', 'listLogs', {identifiers});
	}
};